// src/components/ceremony/StartingSoonCeremony.js
// ============================================================================
// Founder sequence steps 1-3, on one surface:
//   1. registration closes -> countdown ticks for people to get online
//   2. T-5: STARTING SOON
//   3. the ready-up animation, "DYNAMIC per event and per user IGN"
//
// Ported from the approved design (Media/ShowdownDemo/assets/startingsoon.html)
// at its native 1920x1080 coordinates — see CeremonyCanvas.
//
// The demo's ticker was a scripted 0->16 count on a timer. This one is the real
// thing: every chip is an actual entrant, it lights when THAT racer presses
// Ready, and the caption names them. This is the part spectators have never
// been able to see (founder: "users don't see that before the event happens").
// ============================================================================

import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';

import { CEREMONY_COLORS as C, CEREMONY_PHASE, formatCountdown } from '../../constants/ceremony';
import CeremonyCanvas, { CeremonyHeader, anim, IS_WEB } from './CeremonyCanvas';
import SponsorStrip from './SponsorStrip';

// Below this many racers the chips are wide enough to carry the handle itself;
// above it they fall back to grid position, with the caption naming whoever
// just readied. Either way the surface is built from THIS event's field.
const NAME_CHIP_MAX = 8;

// Ticker wrap geometry (design px).
const CHIPS_MAX_W = 1560;
const CHIP_GAP = 14;
// Design-Y of the sponsor strip's top edge — the floor the ticker must clear.
const SPONSOR_TOP = 976;

const CHECK = '✓';

export default function StartingSoonCeremony({
  phase,
  title,
  strapline,
  field = [],
  readyCount = 0,
  totalRequired = 0,
  msUntilStart,
  registrationClosed = false,
  eventId,
  branding = null,
  state = null,
}) {
  const effectiveBranding = branding ?? state?.branding ?? null;
  const isCountdown = phase === CEREMONY_PHASE.COUNTDOWN;
  const total = totalRequired || field.length || 0;
  const allReady = total > 0 && readyCount >= total;

  // Whoever readied most recently — the caption line.
  const lastReady = useMemo(() => {
    const readied = field.filter((f) => f.ready && f.readyAtMs);
    if (!readied.length) return null;
    return readied.reduce((a, b) => (b.readyAtMs > a.readyAtMs ? b : a));
  }, [field]);

  const chips = field.length ? field : Array.from({ length: total }, (_, i) => ({ uid: `slot-${i}`, slot: i + 1 }));

  // Chip metrics scale with the field, and the whole block is then positioned
  // from its own measured height rather than a fixed design-Y. A 16-up keeps
  // the approved composition EXACTLY (one row, ticker at 700, banner at 905);
  // a 50-up Warzone lobby wraps to two rows; a full 150-up wraps to five and
  // the block climbs to stay clear of the sponsor strip at the floor.
  const useNameChips = field.length > 0 && field.length <= NAME_CHIP_MAX;
  const tier = useNameChips
    ? { w: 190, h: 46, gap: CHIP_GAP }
    : total > 60 ? { w: 34, h: 30, gap: 8 }
      : total > 24 ? { w: 50, h: 40, gap: 10 }
        : { w: 64, h: 46, gap: CHIP_GAP };

  const perRow = Math.max(1, Math.floor(CHIPS_MAX_W / (tier.w + tier.gap)));
  const rows = Math.max(1, Math.ceil((total || chips.length) / perRow));

  // count line + gap + wrapped chips + caption (+ the ALL PLAYERS READY banner)
  const blockH = 40 + 22 + (rows * (tier.h + tier.gap) - tier.gap) + 50 + (allReady ? 80 : 0);
  const readyTop = Math.min(700, Math.max(240, SPONSOR_TOP - blockH - 20));
  const soonTop = Math.min(470, readyTop - 180);

  return (
    // The banner montage loops behind the countdown and STARTING SOON — the
    // hype bed, and a showcase of the games the platform runs. Grid lock and
    // the results ceremony stay on the static backdrop, as their approved
    // designs do: once the field is on screen the video would fight it.
    <CeremonyCanvas backdrop="video">
      <CeremonyHeader
        title={title || 'CRITCASH EVENT'}
        subtitle={strapline}
        logoWidth={240}
        top={60}
      />

      {/* --- the headline ---------------------------------------------- */}
      {isCountdown ? (
        <View
          style={[
            styles.headlineWrap,
            { top: soonTop - 40 },
            anim('popIn', { delay: 200, duration: 700 }),
          ]}
        >
          <Text style={styles.kicker}>
            {registrationClosed ? 'REGISTRATION CLOSED' : 'REGISTRATION OPEN'}
          </Text>
          <Text style={styles.clock}>
            {msUntilStart != null ? formatCountdown(msUntilStart) : '--:--'}
          </Text>
          <Text style={styles.kickerDim}>UNTIL START</Text>
        </View>
      ) : (
        /* Two animations, two elements: the wrapper does the one-shot
           entrance, the text does the endless glow pulse. */
        <View
          style={[
            styles.soonWrap,
            { top: soonTop },
            anim('popIn', { delay: 300, duration: 700, easing: 'cubic-bezier(.15,1.2,.3,1)' }),
          ]}
          pointerEvents="none"
        >
          <Text
            style={[
              styles.soon,
              anim('soonPulse', { delay: 1000, duration: 2400, iteration: 'infinite' }),
            ]}
          >
            STARTING SOON
          </Text>
        </View>
      )}

      {/* --- the live ready ticker -------------------------------------- */}
      <View style={[styles.ready, { top: readyTop }, anim('fadeDown', { delay: 600, duration: 700 })]}>
        <Text style={styles.count}>
          PLAYERS READY{' '}
          <Text style={[styles.countNum, allReady && styles.countNumAll]}>{readyCount}</Text>
          <Text style={styles.countTotal}>/{total || '-'}</Text>
        </Text>

        <View style={[styles.chips, { gap: tier.gap }]}>
          {chips.map((r, i) => (
            <ReadyChip
              key={r.uid || i}
              width={tier.w}
              height={tier.h}
              label={useNameChips ? (r.ign || r.name || `P${i + 1}`) : `P${r.slot || i + 1}`}
              ready={!!r.ready}
              // A narrow position chip can't hold "check + P10" — the approved
              // design swaps the label for the check on gold, and the chip's
              // place in the row still says which position it is. Name chips
              // are wide enough to carry both.
              showLabelWhenReady={useNameChips}
            />
          ))}
        </View>

        <Text style={styles.caption} numberOfLines={1}>
          {lastReady ? `${CHECK} ${lastReady.ign || lastReady.name} is ready` : ' '}
        </Text>

        {/* In flow, not pinned to a design-Y: a 50-player battle royale wraps
            the ticker onto three rows, and an absolutely-placed banner would
            sit on top of them. */}
        {allReady && (
          <Text
            style={[
              styles.allReady,
              anim('popIn', { duration: 500, easing: 'cubic-bezier(.15,1.3,.3,1)' }),
            ]}
          >
            ALL PLAYERS READY
          </Text>
        )}
      </View>

      <SponsorStrip eventId={eventId} bottom={34} branding={effectiveBranding} />
    </CeremonyCanvas>
  );
}

function ReadyChip({ label, ready, width, height, showLabelWhenReady }) {
  const text = ready
    ? (showLabelWhenReady ? `${CHECK} ${label}` : CHECK)
    : label;
  return (
    <View style={[styles.chip, { width, height }, ready && styles.chipReady]}>
      <Text
        style={[
          styles.chipText,
          ready && styles.chipTextReady,
          ready && !showLabelWhenReady && styles.chipCheckOnly,
        ]}
        numberOfLines={1}
      >
        {text}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  headlineWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  kicker: {
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: 8,
    color: C.CYAN_SOFT,
  },
  kickerDim: {
    marginTop: 6,
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: 8,
    color: C.DIM,
  },
  clock: {
    marginTop: 10,
    fontSize: 130,
    fontWeight: '900',
    letterSpacing: 6,
    color: '#ffffff',
    ...(IS_WEB
      ? { textShadow: '0 0 60px rgba(252,208,0,0.45), 0 4px 24px rgba(0,0,0,0.9)' }
      : {}),
  },
  soonWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  soon: {
    textAlign: 'center',
    fontSize: 110,
    fontWeight: '900',
    letterSpacing: 26,
    color: '#ffffff',
    ...(IS_WEB
      ? { textShadow: '0 0 60px rgba(252,208,0,0.55), 0 4px 24px rgba(0,0,0,0.9)' }
      : {}),
  },
  ready: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  count: {
    fontSize: 30,
    fontWeight: '900',
    letterSpacing: 6,
    color: '#cfcfcf',
  },
  countNum: { color: C.GOLD, fontSize: 34 },
  countNumAll: { color: C.GREEN },
  countTotal: { color: '#777777' },
  chips: {
    marginTop: 22,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    maxWidth: CHIPS_MAX_W,
    gap: CHIP_GAP,
  },
  chip: {
    borderRadius: 9,
    backgroundColor: 'rgba(10,10,10,0.72)',
    borderWidth: 1,
    borderColor: '#3a3a3a',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  chipReady: {
    backgroundColor: C.GOLD,
    borderColor: C.GOLD_BRIGHT,
    ...(IS_WEB ? { boxShadow: '0 0 22px rgba(252,208,0,0.6)' } : {}),
  },
  chipText: {
    fontSize: 17,
    fontWeight: '900',
    color: '#666666',
  },
  chipTextReady: { color: '#111111' },
  chipCheckOnly: { fontSize: 24 },
  caption: {
    marginTop: 20,
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: 2,
    color: C.GREEN,
    minHeight: 30,
  },
  allReady: {
    marginTop: 40,   // lands at ~design-Y 905 for a 16-racer grid
    textAlign: 'center',
    fontSize: 30,
    fontWeight: '900',
    letterSpacing: 10,
    color: C.GREEN,
    ...(IS_WEB ? { textShadow: '0 0 30px rgba(126,231,135,0.55)' } : {}),
  },
});
